/**
 * Veyronix Commerce — client-facing pages: Playbook, Proposal, Agreement e-sign, Invoice payment.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { API } from "@/App";
import { CheckCircle, Download, FileText, Loader2, CreditCard, ShieldCheck, BookOpen } from "lucide-react";
import { Shell } from "@/pages/CommercePages";

const backendBase = () => API.replace(/\/api$/, "");
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const post = async (path, body) => {
  const res = await fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong");
  return data;
};

const usePublic = (path) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const reload = () => fetch(`${API}${path}`).then(async r => {
    const d = await r.json().catch(() => ({}));
    setState({ loading: false, data: r.ok ? d : null, error: r.ok ? null : (d.detail || "Not found") });
  });
  useEffect(() => { reload(); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...state, reload };
};

const Card = ({ children, testid }) => (
  <div className="max-w-3xl mx-auto px-6 py-14"><div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-10" data-testid={testid}>{children}</div></div>
);

const Status = ({ loading, error, children }) => {
  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  if (error) return <Card testid="commerce-public-error"><p className="text-center text-slate-600">{error}</p></Card>;
  return children;
};

// ============ PLAYBOOK ============
const PLAYBOOK_POINTS = ["UK legal set-up, VAT & consumer law essentials", "Supplier vetting checklist", "Pricing-for-profit worked example", "Store, fulfilment & launch marketing plan", "KPIs and a 90-day launch checklist"];

const PlaybookSuccess = ({ url }) => (
  <div className="text-center py-6" data-testid="playbook-success">
    <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
    <h2 className="text-xl font-bold mb-2">Your playbook is ready</h2>
    <p className="text-slate-500 text-sm mb-6">We've also emailed you a copy of the download link.</p>
    <a href={url} data-testid="playbook-download-link"><Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-6"><Download className="h-4 w-4 mr-2" />Download PDF</Button></a>
    <p className="text-sm text-slate-500 mt-8">Want help applying it? <Link to="/commerce/book-consultation" className="text-indigo-600 font-medium">Book a free consultation</Link></p>
  </div>
);

export const CommercePlaybook = () => {
  const [form, setForm] = useState({ full_name: "", email: "", company_name: "", privacy_consent: false, marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  useEffect(() => { document.title = "Free UK Dropshipping Playbook | Veyronix Commerce"; }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) return toast.error("Please accept the privacy notice");
    setBusy(true);
    try {
      const r = await post("/commerce/playbook", { ...form, company_name: form.company_name || null, origin_url: window.location.origin });
      setUrl(`${backendBase()}${r.download_url}`);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  return (
    <Shell>
      <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs font-semibold tracking-widest text-emerald-600 mb-3 flex items-center gap-2"><BookOpen className="h-4 w-4" />FREE PDF GUIDE</p>
          <h1 className="font-brand text-4xl sm:text-5xl font-bold text-slate-900 mb-4">The UK Dropshipping Playbook</h1>
          <p className="text-slate-600 mb-8">A practical, UK-specific guide to launching a compliant and profitable dropshipping business — written by the Veyronix Commerce team.</p>
          <ul className="space-y-3">{PLAYBOOK_POINTS.map(p => <li key={p} className="flex gap-3 text-slate-700"><CheckCircle className="h-5 w-5 text-emerald-500 shrink-0" />{p}</li>)}</ul>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
          {url ? <PlaybookSuccess url={url} /> : (
            <form onSubmit={submit} className="space-y-4" data-testid="playbook-form">
              <div className="space-y-1.5"><Label>Full name *</Label><Input required value={form.full_name} onChange={set("full_name")} data-testid="playbook-name" /></div>
              <div className="space-y-1.5"><Label>Email *</Label><Input required type="email" value={form.email} onChange={set("email")} data-testid="playbook-email" /></div>
              <div className="space-y-1.5"><Label>Company (optional)</Label><Input value={form.company_name} onChange={set("company_name")} data-testid="playbook-company" /></div>
              <label className="flex gap-3 text-sm text-slate-600 items-start">
                <Checkbox checked={form.privacy_consent} onCheckedChange={v => setForm(p => ({ ...p, privacy_consent: !!v }))} data-testid="playbook-privacy-consent" />
                <span>I agree that Veyronix Commerce may process my details to send me the playbook, as described in the <Link to="/privacy" className="text-indigo-600">privacy notice</Link>. *</span>
              </label>
              <label className="flex gap-3 text-sm text-slate-600 items-start">
                <Checkbox checked={form.marketing_consent} onCheckedChange={v => setForm(p => ({ ...p, marketing_consent: !!v }))} data-testid="playbook-marketing-consent" />
                <span>Optional: send me occasional e-commerce tips and Veyronix Commerce updates. Unsubscribe any time.</span>
              </label>
              <Button type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="playbook-submit">{busy ? "Preparing…" : "Get the free playbook"}</Button>
            </form>
          )}
        </div>
      </section>
    </Shell>
  );
};

// ============ PROPOSAL ============
export const CommerceProposalView = () => {
  const { token } = useParams();
  const { loading, error, data: p, reload } = usePublic(`/commerce/proposal/${token}`);
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    if (kind === "decline" && !window.confirm("Decline this proposal?")) return;
    setBusy(true);
    try {
      const r = await post(`/commerce/proposal/${token}/${kind}`, { origin_url: window.location.origin });
      if (r.agreement_token) window.location.href = `/commerce/agreement/${r.agreement_token}`;
      else { toast.success("Proposal declined"); reload(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Shell><Status loading={loading} error={error}>{p && (
      <Card testid="proposal-view">
        <p className="text-xs font-semibold tracking-widest text-indigo-600 mb-2">PROPOSAL</p>
        <h1 className="text-3xl font-bold text-slate-900 mb-1" data-testid="proposal-view-title">{p.title}</h1>
        <p className="text-slate-500 mb-8">Prepared for {p.client_name}{p.client_company ? `, ${p.client_company}` : ""}</p>
        <h2 className="font-semibold mb-2">Scope of work</h2>
        <p className="whitespace-pre-wrap text-slate-700 mb-8">{p.scope}</p>
        <div className="flex items-center justify-between rounded-xl bg-slate-50 p-5 mb-8">
          <span className="text-slate-600">{p.billing_type === "monthly" ? "Monthly fee" : "Project fee"} (ex VAT)</span>
          <span className="text-2xl font-bold" data-testid="proposal-view-price">{gbp(p.price_amount)}{p.billing_type === "monthly" ? "/mo" : ""}</span>
        </div>
        {p.status === "sent" && (
          <div className="flex flex-col sm:flex-row gap-3">
            <Button disabled={busy} onClick={() => act("accept")} className="bg-indigo-600 hover:bg-indigo-700 rounded-full flex-1" data-testid="proposal-accept-btn">Accept & continue to agreement</Button>
            <Button disabled={busy} variant="outline" onClick={() => act("decline")} className="rounded-full" data-testid="proposal-decline-btn">Decline</Button>
          </div>
        )}
        {p.status === "accepted" && p.agreement && (
          <div className="text-center" data-testid="proposal-accepted">
            <p className="text-emerald-600 font-medium mb-3">Proposal accepted</p>
            <a href={p.agreement.public_url}><Button className="rounded-full bg-indigo-600 hover:bg-indigo-700">{p.agreement.status === "signed" ? "View signed agreement" : "Sign agreement"}</Button></a>
          </div>
        )}
        {p.status === "declined" && <p className="text-center text-rose-600" data-testid="proposal-declined">This proposal was declined.</p>}
      </Card>
    )}</Status></Shell>
  );
};

// ============ AGREEMENT E-SIGN ============
const SignaturePad = ({ canvasRef, onDraw }) => {
  const drawing = useRef(false);
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return [(t.clientX - r.left) * (canvasRef.current.width / r.width), (t.clientY - r.top) * (canvasRef.current.height / r.height)];
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    ctx.lineTo(...pos(e)); ctx.stroke(); onDraw();
  };
  const end = () => { drawing.current = false; };
  return <canvas ref={canvasRef} width={600} height={160} className="w-full h-40 border border-slate-300 rounded-lg bg-white touch-none" data-testid="agreement-signature-canvas"
    onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />;
};

const SignForm = ({ token, onSigned, defaultName }) => {
  const [type, setType] = useState("typed");
  const [name, setName] = useState(defaultName || "");
  const [typed, setTyped] = useState("");
  const [agree, setAgree] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const sign = async () => {
    const signature = type === "typed" ? typed : drawn && canvasRef.current.toDataURL("image/png");
    if (!name || !signature || !agree) return toast.error("Enter your name, sign and accept the terms");
    setBusy(true);
    try { await post(`/commerce/agreement/${token}/sign`, { signer_name: name, signature_type: type, signature_data: signature, agree }); toast.success("Agreement signed"); onSigned(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 border-t border-slate-100 pt-6" data-testid="agreement-sign-form">
      <div className="space-y-1.5"><Label>Full legal name *</Label><Input value={name} onChange={e => setName(e.target.value)} data-testid="agreement-signer-name" /></div>
      <div className="flex gap-2">
        {["typed", "drawn"].map(t => <Button key={t} type="button" size="sm" variant={type === t ? "default" : "outline"} onClick={() => setType(t)} data-testid={`agreement-sig-${t}`}>{t === "typed" ? "Type signature" : "Draw signature"}</Button>)}
      </div>
      {type === "typed"
        ? <Input value={typed} onChange={e => setTyped(e.target.value)} placeholder="Type your signature" className="text-2xl italic font-serif h-14" data-testid="agreement-typed-signature" />
        : <SignaturePad canvasRef={canvasRef} onDraw={() => setDrawn(true)} />}
      <label className="flex gap-3 text-sm text-slate-600 items-start">
        <Checkbox checked={agree} onCheckedChange={v => setAgree(!!v)} data-testid="agreement-agree-checkbox" />
        <span>I have read and agree to this agreement and the Veyronix Commerce <Link to="/consultancy-terms" className="text-indigo-600">Consultancy Terms</Link>, and I understand this electronic signature is legally binding.</span>
      </label>
      <Button disabled={busy} onClick={sign} className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="agreement-sign-btn">{busy ? "Signing…" : "Sign agreement"}</Button>
    </div>
  );
};

export const CommerceAgreementSign = () => {
  const { token } = useParams();
  const { loading, error, data: a, reload } = usePublic(`/commerce/agreement/${token}`);
  return (
    <Shell><Status loading={loading} error={error}>{a && (
      <Card testid="agreement-view">
        <p className="text-xs font-semibold tracking-widest text-indigo-600 mb-2 flex items-center gap-2"><FileText className="h-4 w-4" />SERVICES AGREEMENT</p>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">{a.title}</h1>
        <p className="text-slate-500 mb-6">Between Veyronix Limited (t/a Veyronix Commerce) and {a.client_name}{a.client_company ? `, ${a.client_company}` : ""}</p>
        <h2 className="font-semibold mb-2">Scope of work</h2>
        <p className="whitespace-pre-wrap text-slate-700 mb-6">{a.scope}</p>
        <p className="mb-6"><span className="text-slate-500">Fee: </span><b>{gbp(a.price_amount)}{a.billing_type === "monthly" ? " per month" : " one-off"}</b> <span className="text-slate-400 text-sm">(plus VAT where applicable)</span></p>
        <h2 className="font-semibold mb-2">Terms</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-600 mb-6 bg-slate-50 rounded-xl p-4">{a.terms}</p>
        {a.status === "signed" ? (
          <div className="text-center py-4" data-testid="agreement-signed">
            <ShieldCheck className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold">Signed by {a.signer_name}</p>
            <p className="text-sm text-slate-500">{new Date(a.signed_at).toLocaleString("en-GB")} — your invoice will follow by email.</p>
          </div>
        ) : <SignForm token={token} onSigned={reload} defaultName={a.client_name} />}
      </Card>
    )}</Status></Shell>
  );
};

// ============ INVOICE PAYMENT ============
export const CommercePayInvoice = () => {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { loading, error, data: inv, reload } = usePublic(`/commerce/invoice/${token}`);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(!!sessionId);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      const r = await fetch(`${API}/commerce/invoice/${token}/status/${sessionId}`).then(x => x.json()).catch(() => ({}));
      if (r.payment_status === "paid" || ++tries >= 6) { setChecking(false); reload(); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async () => {
    setBusy(true);
    try { const r = await post(`/commerce/invoice/${token}/checkout`, { origin_url: window.location.origin }); window.location.href = r.url; }
    catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <Shell><Status loading={loading} error={error}>{inv && (
      <Card testid="invoice-pay-view">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div><p className="text-xs font-semibold tracking-widest text-indigo-600">INVOICE</p><h1 className="text-2xl font-bold" data-testid="invoice-pay-number">{inv.invoice_number}</h1>
            <p className="text-sm text-slate-500">Issued {inv.issue_date} · Due {inv.due_date}</p></div>
          <div className="text-right text-sm text-slate-500"><p className="font-medium text-slate-800">{inv.company_name}</p><p>{inv.company_email}</p></div>
        </div>
        <p className="text-sm text-slate-500 mb-2">Billed to <b className="text-slate-800">{inv.client_name}</b></p>
        <table className="w-full text-sm mb-6"><tbody>
          {inv.items.map((it, i) => <tr key={i} className="border-b border-slate-100"><td className="py-2">{it.description} × {it.quantity}</td><td className="py-2 text-right">{gbp(it.amount)}</td></tr>)}
          <tr><td className="pt-3 text-slate-500">Subtotal</td><td className="pt-3 text-right">{gbp(inv.subtotal)}</td></tr>
          <tr><td className="text-slate-500">VAT ({inv.tax_rate}%)</td><td className="text-right">{gbp(inv.tax_amount)}</td></tr>
          {inv.amount_paid > 0 && <tr><td className="text-slate-500">Paid</td><td className="text-right">−{gbp(inv.amount_paid)}</td></tr>}
          <tr><td className="pt-2 font-bold">Amount due</td><td className="pt-2 text-right text-xl font-bold" data-testid="invoice-pay-due">{gbp(inv.total - (inv.amount_paid || 0))}</td></tr>
        </tbody></table>
        {checking ? <p className="text-center text-slate-500 flex items-center justify-center gap-2" data-testid="invoice-pay-checking"><Loader2 className="h-4 w-4 animate-spin" />Confirming your payment…</p>
          : inv.status === "paid" ? (
            <div className="text-center" data-testid="invoice-paid"><CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-2" /><p className="font-semibold">Paid — thank you!</p></div>
          ) : inv.status === "cancelled" ? <p className="text-center text-rose-600">This invoice has been cancelled.</p> : (
            <>
              <Button disabled={busy} onClick={pay} className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="invoice-pay-card-btn"><CreditCard className="h-4 w-4 mr-2" />{busy ? "Redirecting…" : "Pay securely by card"}</Button>
              {inv.notes && <div className="mt-6 text-sm text-slate-600"><p className="font-medium mb-1">Prefer bank transfer?</p><p className="whitespace-pre-wrap">{inv.notes}</p></div>}
            </>
          )}
      </Card>
    )}</Status></Shell>
  );
};
